import {Injectable} from '@angular/core';
import {SystemApi} from '@derena/api';
import {BehaviorSubject, Observable} from 'rxjs';

type CachedInstance = { name: string | null; fetchedAt: number };

const CACHE_KEY = 'instance-info';
const CACHE_TTL_MS = 60 * 60 * 1000;

/**
 * Lädt den Namen der Instanz (öffentlicher Endpunkt, kein Login nötig) und cached ihn
 * im sessionStorage. Eine neue Session oder ein abgelaufener Cache löst einen neuen Request aus.
 */
@Injectable({ providedIn: 'root' })
export class InstanceService {
    private nameSubject = new BehaviorSubject<string | null>(null);
    private loading: Promise<void> | null = null;

    constructor(private systemApi: SystemApi) {
        const cached = this.readCache();
        if (cached) {
            this.nameSubject.next(cached.name);
        }
    }

    get name(): string | null {
        return this.nameSubject.value;
    }

    name$(): Observable<string | null> {
        return this.nameSubject.asObservable();
    }

    /** Lädt den Instanznamen, falls kein gültiger Cache vorliegt. */
    load(): Promise<void> {
        if (this.readCache()) {
            return Promise.resolve();
        }

        if (!this.loading) {
            this.loading = this.fetch().then(() => { this.loading = null; });
        }

        return this.loading;
    }

    private async fetch(): Promise<void> {
        try {
            const info = await this.systemApi.info().toPromise();
            const name = info && info.name ? String(info.name) : null;
            this.writeCache({ name, fetchedAt: Date.now() });
            this.nameSubject.next(name);
        } catch (e) {
            console.log('error loading instance info', e);
        }
    }

    private readCache(): CachedInstance | null {
        try {
            const cached: CachedInstance = JSON.parse(sessionStorage.getItem(CACHE_KEY));
            return cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS ? cached : null;
        } catch (e) {
            return null;
        }
    }

    private writeCache(value: CachedInstance) {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
        } catch (e) { }
    }
}
